import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryIcon } from '@monorepo/nova-icons';
import {
  Box,
  IconButton,
  MenuItem,
  Typography,
  useTheme,
} from '@monorepo/nova-ui';
import {
  getLanguageDetails,
  LANGUAGE_DETAILS,
  LanguageOption,
} from '@monorepo/types';
import { useLanguage } from '../../../../../hooks';
import {
  StyledLanguageMenu,
  StyledMenuItemIcon,
} from './language-selector.styled';

export const LanguageSelector = () => {
  const { i18n } = useTranslation(['hubCommon']);
  const { palette, spacing } = useTheme();
  const { changeLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentLanguage = i18n.resolvedLanguage ?? LanguageOption.ENGLISH;
  const languageDetails = getLanguageDetails(currentLanguage);

  const filteredLanguageOptions = Object.entries(LANGUAGE_DETAILS).filter(
    ([lang]) => lang !== i18n.language,
  );

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: LanguageOption) => {
    changeLanguage(languageCode);
    handleClose();
  };

  return (
    <>
      <Box>
        <IconButton
          onClick={handleButtonClick}
          aria-label="Select language"
          sx={{ p: 1.75 }}
        >
          <CountryIcon
            code={languageDetails.countryCode}
            sx={{
              width: spacing(6.5),
              height: spacing(6.5),
              borderRadius: '50%',
              border: `1px solid ${palette.divider}`,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
            }}
          />
        </IconButton>
      </Box>
      <StyledLanguageMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
      >
        {filteredLanguageOptions.map(([lang, details]) => (
          <MenuItem
            key={lang}
            onClick={() => handleLanguageChange(lang as LanguageOption)}
          >
            <StyledMenuItemIcon>
              <CountryIcon code={details.countryCode} />
            </StyledMenuItemIcon>
            <Box flex={1}>
              <Typography
                variant="body2"
                paddingTop={spacing(0.25)}
                sx={{
                  color: palette.text.second,
                }}
              >
                {details.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </StyledLanguageMenu>
    </>
  );
};
